.
<template>
    <transition name="indicator">
        <div
            v-if="loading"
            class="progress-indicator-linear"
        >
            <div
                class="progress indeterminate four-color"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="1"
            >
                <div class="inactive-track"></div>

                <div class="bar primary-bar">
                    <div class="bar-inner"></div>
                </div>

                <div class="bar secondary-bar">
                    <div class="bar-inner"></div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script setup lang="ts">
defineProps<{
    loading: boolean
}>()
</script>

<style scoped>
.progress-indicator-linear {
    --_active-indicator-height: 4px;
    --_four-color-active-indicator-four-color: var(--taaffeite);
    --_four-color-active-indicator-one-color: var(--blush);
    --_four-color-active-indicator-three-color: var(--blush);
    --_four-color-active-indicator-two-color: var(--taaffeite);
    --_track-color: #e6e0e9;
    --_track-height: 4px;
    --_track-shape: 0;
    position: absolute;
    bottom: 0;
    border-radius: var(--_track-shape);
    display: flex;
    width: 100%;
    min-width: 80px;
    height: var(--_track-height);
    content-visibility: auto;
    contain: strict;

    .progress,
    .dots,
    .inactive-track,
    .bar,
    .bar-inner {
        position: absolute;
    }

    .inactive-track,
    .bar .primary-bar {
        transform: scaleX(100%);
    }

    .progress {
        direction: ltr;
        inset: 0;
        border-radius: inherit;
        overflow: hidden;
        display: flex;
        align-items: center;
    }

    .bar {
        animation: none;
        width: 100%;
        height: var(--_active-indicator-height);
        transform-origin: left center;
        transition: transform 250ms cubic-bezier(0.4, 0, 0.6, 1);

        .bar-inner {
            inset: 0;
            animation: none;
        }
    }

    .secondary-bar {
        display: none;
    }

    .inactive-track {
        background: var(--_track-color);
        inset: 0;
        transition: transform 250ms cubic-bezier(0.4, 0, 0.6, 1);
        transform-origin: left center;
    }

    .indeterminate {
        .bar {
            transition: none;

            &.primary-bar {
                inset-inline-start: -145.167%;
                animation: linear infinite 2s;
                animation-name: primary-indeterminate-translate;
            }
        }
    }

    .indeterminate .primary-bar {
        inset-inline-start: -145.167%;
        animation: linear infinite 2s;
        animation-name: primary-indeterminate-translate;
    }

    .indeterminate .secondary-bar {
        inset-inline-start: -54.8889%;
        display: block;
        animation: linear infinite 2s;
        animation-name: secondary-indeterminate-translate;
    }

    .indeterminate .primary-bar > .bar-inner {
        animation: linear infinite 2s primary-indeterminate-scale;
    }

    .indeterminate.four-color .primary-bar > .bar-inner {
        animation-name: primary-indeterminate-scale, four-color;
        animation-duration: 2s, 4s;
    }

    .indeterminate .secondary-bar > .bar-inner {
        animation: linear infinite 2s secondary-indeterminate-scale;
    }

    .indeterminate.four-color .secondary-bar > .bar-inner {
        animation-name: secondary-indeterminate-scale, four-color;
        animation-duration: 2s, 4s;
    }

    @media (forced-colors: active) {
        :host {
            outline: 1px solid CanvasText;
        }
        .bar-inner,
        .dots {
            background-color: CanvasText;
        }
    }
}

@keyframes primary-indeterminate-scale {
    0% {
        transform: scaleX(0.08);
    }
    36.65% {
        animation-timing-function: cubic-bezier(0.334731, 0.12482, 0.785844, 1);
        transform: scaleX(0.08);
    }
    69.15% {
        animation-timing-function: cubic-bezier(0.06, 0.11, 0.6, 1);
        transform: scaleX(0.661479);
    }
    100% {
        transform: scaleX(0.08);
    }
}
@keyframes secondary-indeterminate-scale {
    0% {
        animation-timing-function: cubic-bezier(
            0.205028,
            0.057051,
            0.57661,
            0.453971
        );
        transform: scaleX(0.08);
    }
    19.15% {
        animation-timing-function: cubic-bezier(
            0.152313,
            0.196432,
            0.648374,
            1.00432
        );
        transform: scaleX(0.457104);
    }
    44.15% {
        animation-timing-function: cubic-bezier(
            0.257759,
            -0.003163,
            0.211762,
            1.38179
        );
        transform: scaleX(0.72796);
    }
    100% {
        transform: scaleX(0.08);
    }
}
@keyframes buffering {
    0% {
        transform: translateX(calc(var(--_track-height) / 2 * 5));
    }
}
@keyframes primary-indeterminate-translate {
    0% {
        transform: translateX(0px);
    }
    20% {
        animation-timing-function: cubic-bezier(0.5, 0, 0.701732, 0.495819);
        transform: translateX(0px);
    }
    59.15% {
        animation-timing-function: cubic-bezier(
            0.302435,
            0.381352,
            0.55,
            0.956352
        );
        transform: translateX(83.6714%);
    }
    100% {
        transform: translateX(200.611%);
    }
}
@keyframes secondary-indeterminate-translate {
    0% {
        animation-timing-function: cubic-bezier(0.15, 0, 0.515058, 0.409685);
        transform: translateX(0px);
    }
    25% {
        animation-timing-function: cubic-bezier(
            0.31033,
            0.284058,
            0.8,
            0.733712
        );
        transform: translateX(37.6519%);
    }
    48.35% {
        animation-timing-function: cubic-bezier(0.4, 0.627035, 0.6, 0.902026);
        transform: translateX(84.3862%);
    }
    100% {
        transform: translateX(160.278%);
    }
}
@keyframes four-color {
    0% {
        background: var(--_four-color-active-indicator-one-color);
    }
    15% {
        background: var(--_four-color-active-indicator-one-color);
    }
    25% {
        background: var(--_four-color-active-indicator-two-color);
    }
    40% {
        background: var(--_four-color-active-indicator-two-color);
    }
    50% {
        background: var(--_four-color-active-indicator-three-color);
    }
    65% {
        background: var(--_four-color-active-indicator-three-color);
    }
    75% {
        background: var(--_four-color-active-indicator-four-color);
    }
    90% {
        background: var(--_four-color-active-indicator-four-color);
    }
    100% {
        background: var(--_four-color-active-indicator-one-color);
    }
}

.indicator-enter-active {
    transition: all 0.25s ease;
}

.indicator-leave-active {
    transition: all 0.65s ease;
}

.indicator-enter-from,
.indicator-leave-to {
    height: 0;
    opacity: 0;
}
</style>
